import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useState, useRef, useEffect, useCallback } from 'react';
import { twMerge } from '@belong/common';
import { IconLeftCaret, IconRightCaret } from '@belong/icons';
import clsx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { ButtonBase } from '../ButtonBase/ButtonBase.js';
import { Text } from '../Text/Text.js';
import css from './Carousel.module.css.js';

function Carousel({
  children,
  onChange = () => {},
  startIndex = 0,
  variant = 'default',
  autoplay = false,
  autoplayOptions = {},
  align = 'start',
  loop = false,
  slidesToScroll = 1,
  arrowsOnDots = false,
  blurredEnds = true,
  hideScrollSnaps = false,
  customStyles = {},
  breakpoints,
  decoration,
  theme = 'light',
  dotsWrapperClassname,
  containScroll,
  onApi
}) {
  const plugins = [];
  if (autoplay) plugins.push(Autoplay(autoplayOptions));
  const maxNumberOfDots = 7;
  const [viewportRef, embla] = useEmblaCarousel(
  // `loop` was declared by the prop type (via `EmblaOptionsType`) but never
  // forwarded, so passing it did nothing and a slider that asked to wrap
  // still stopped dead at both ends. Defaults to `false`, so no existing
  // caller changes behaviour.
  {
    align,
    loop,
    slidesToScroll,
    startIndex,
    breakpoints,
    containScroll
  }, plugins);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(startIndex);
  const previousSelectedIndexRef = useRef(startIndex);
  const firstDotIndexRef = useRef(startIndex);
  const lastDotIndexRef = useRef(maxNumberOfDots);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const slidesContainerRef = useRef(null);
  const styles = {
    leftArrow: '-left-2sm',
    rightArrow: '-right-2sm',
    ...customStyles
  };
  useEffect(() => {
    if (embla) {
      onApi?.(embla);
    }
  }, [embla, onApi]);
  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
  const canScrollPrev = useCallback(() => embla && embla.canScrollPrev(), [embla]);
  const canScrollNext = useCallback(() => embla && embla.canScrollNext(), [embla]);
  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(previousState => {
      previousSelectedIndexRef.current = previousState;
      const nextState = embla.selectedScrollSnap();
      // Swiping right
      if (previousState < nextState) {
        // Only move range to the right, when the selectedIndex is on the last big dot.
        if (nextState === lastDotIndexRef.current - 1) {
          ++firstDotIndexRef.current;
          ++lastDotIndexRef.current;
        }
        // Swiping Left
      } else if (previousState > nextState) {
        // Only move range to the left, when the selectedIndex is on the first big dot.
        if (nextState === firstDotIndexRef.current) {
          --firstDotIndexRef.current;
          --lastDotIndexRef.current;
        }
      }
      onChange(nextState);
      return nextState;
    });
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
    setScrollSnaps(embla.scrollSnapList());
  }, [embla, setSelectedIndex, setPrevBtnEnabled, setNextBtnEnabled, setScrollSnaps]);
  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on('select', onSelect);
    embla.on('resize', onSelect);
  }, [embla, onSelect]);
  useEffect(() => {
    if (slidesContainerRef?.current) {
      document.addEventListener('keyup', handleKeyUp);
      slidesContainerRef.current.addEventListener('click', captureClick);
    }
    return () => {
      if (slidesContainerRef?.current) {
        slidesContainerRef.current.removeEventListener('click', captureClick);
        document.removeEventListener('keyup', handleKeyUp);
      }
    };
  }, [embla]);
  // Enables keyboard control when the carousel is focused.
  function handleKeyUp(event) {
    if (!embla) return;
    const focusedCarousel = document.activeElement === slidesContainerRef?.current;
    if (!focusedCarousel) return;
    switch (event.code) {
      case 'ArrowLeft':
        embla.scrollPrev();
        break;
      case 'ArrowRight':
        embla.scrollNext();
        break;
    }
  }
  // Prevent clicks from triggering when sliding from a slide with a click handler.
  function captureClick(e) {
    if (embla && !embla.clickAllowed()) {
      e.stopPropagation();
    }
  }
  const len = scrollSnaps.length;
  const prevSaretClassName = getScaretClassName(theme, canScrollPrev());
  const nextSaretClassName = getScaretClassName(theme, canScrollNext());
  return jsxs("div", {
    className: clsx('relative isolate flex w-full items-center', blurredEnds ? css.emblaCarousel : {}),
    children: [typeof decoration === 'function' ? decoration(canScrollPrev, canScrollNext) : decoration, jsxs("div", {
      className: "w-full overflow-hidden",
      ref: viewportRef,
      children: [jsx("div", {
        className: clsx(
        // `touch-pan-y` is what Embla asks for on the slide container: it
        // leaves vertical scrolling to the page and hands horizontal
        // gestures to the carousel, instead of the browser claiming a
        // near-horizontal swipe and the drag never starting.
        'flex w-full touch-pan-y outline-none', variant === 'default' ? 'mb-sm' : 'mb-lg'),
        ref: slidesContainerRef,
        tabIndex: 0,
        children: children
      }), jsxs("div", {
        className: clsx('flex w-full items-center justify-center', dotsWrapperClassname),
        children: [arrowsOnDots && jsx("div", {
          className: "mr-xl",
          children: jsx(ButtonBase, {
            name: "previous-button",
            onClick: scrollPrev,
            disabled: !prevBtnEnabled || !canScrollPrev(),
            trackingData: {
              component: 'carousel',
              index: selectedIndex
            },
            type: "button",
            children: jsx(IconLeftCaret, {
              className: prevSaretClassName,
              width: 15,
              height: 12
            })
          })
        }), !hideScrollSnaps && jsxs("div", {
          className: clsx('relative flex items-center', variant === 'numbered' && 'gap-x-xs lg:gap-x-sm'),
          children: [variant === 'numbered' && jsx("div", {
            className: "absolute -z-10 h-0 w-full border-0 border-t-2 border-dashed border-gray"
          }), scrollSnaps.map((_, index) => {
            if (variant === 'numbered') {
              // TODO: define styles for dark theme
              return jsx(ButtonBase, {
                className: twMerge('w-sm h-sm flex items-center justify-center rounded-full border-2 border-solid border-navy', index === selectedIndex ? 'bg-navy' : 'bg-white'),
                onClick: () => embla?.scrollTo(index),
                children: jsx(Text, {
                  variant: "p1",
                  fontWeight: "semibold",
                  className: clsx({
                    'text-white': index === selectedIndex
                  }),
                  children: index + 1
                })
              }, index);
            }
            // Default class, show as big dots.
            let greenDotClassName = 'w-2sm h-2sm p-2xs';
            if (index === firstDotIndexRef.current && index > 0 || index === lastDotIndexRef.current - 1 && index < len - 1) {
              // First and last dot in view are smaller except for the first and last slide.
              greenDotClassName = 'w-[12px] h-[12px] p-2xs';
            } else if (index < firstDotIndexRef.current || index >= lastDotIndexRef.current) {
              // Hide the dots not in view
              greenDotClassName = 'w-0 h-0';
            }
            return jsx("div", {
              className: clsx('transition-all duration-hover ease-in-out', greenDotClassName),
              children: jsx("div", {
                className: twMerge(clsx('h-full w-full rounded-full bg-lighter-green', {
                  'bg-green': index === selectedIndex
                }))
              })
            }, index);
          })]
        }), arrowsOnDots && jsx("div", {
          className: "ml-xl",
          children: jsx(ButtonBase, {
            name: "next-button",
            onClick: scrollNext,
            disabled: !nextBtnEnabled || !canScrollNext(),
            trackingData: {
              component: 'carousel',
              index: selectedIndex
            },
            type: "button",
            children: jsx(IconRightCaret, {
              className: nextSaretClassName,
              width: 15,
              height: 12
            })
          })
        })]
      })]
    }), !arrowsOnDots && jsxs(Fragment, {
      children: [jsx(ButtonBase, {
        name: "previous-button",
        className: clsx('invisible absolute lg:visible', styles.leftArrow),
        onClick: scrollPrev,
        disabled: !prevBtnEnabled || !canScrollPrev(),
        trackingData: {
          component: 'carousel',
          index: selectedIndex
        },
        type: "button",
        children: jsx(IconLeftCaret, {
          className: prevSaretClassName,
          width: 15
        })
      }), jsx(ButtonBase, {
        name: "next-button",
        className: clsx('invisible absolute lg:visible', styles.rightArrow),
        onClick: scrollNext,
        disabled: !nextBtnEnabled || !canScrollNext(),
        trackingData: {
          component: 'carousel',
          index: selectedIndex
        },
        type: "button",
        children: jsx(IconRightCaret, {
          className: nextSaretClassName,
          width: 15
        })
      })]
    })]
  });
}
const getScaretClassName = (theme, enabled) => {
  const isLightTheme = theme === 'light';
  const isDarkTheme = theme === 'dark';
  return clsx({
    'text-navy': isLightTheme && enabled || isDarkTheme && !enabled,
    'text-gray': isLightTheme && !enabled,
    'hover:text-dark-navy': isLightTheme && enabled,
    'hover:text-gray': isLightTheme && !enabled,
    'text-white': isDarkTheme && enabled,
    'hover:text-navy': isDarkTheme && !enabled,
    'hover:text-dark-gray': isDarkTheme && enabled
  });
};

export { Carousel };
